import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { ScenarioResponseDto } from '@cloud-twin-v2/generated/simulation.generated.client';
import { GridSetting } from '@common-modules/shared/constants/grid.constants';
import { GridSettingsService } from '@common-modules/shared/core/grid/grid-settings.service';
import { LocalGridComponent } from '@common-modules/wlm-grid/local-grid/local-grid.component';
import { SpinnerService } from '@common-modules/wlm-spinner/spinner.service';

import { DialogService } from '@common-modules/shared/dialogs/dialogs.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize, of, switchMap } from 'rxjs';
import { ModelVersionSharedService } from '../services/model-version-shared.service';
import { ModelVersionService } from '../services/model-version.service';

@Component({
  selector: 'ct-model-version-scenarios-grid',
  templateUrl: './model-version-scenarios-grid.component.html',
  styleUrl: './model-version-scenarios-grid.component.scss',
  standalone: false,
})
export class ModelVersionScenariosGridComponent implements OnInit {
  @Input() scenarios: ScenarioResponseDto[] = [];
  @ViewChild(LocalGridComponent) grid: LocalGridComponent;

  readonly gridSettingKey = 'ModelVersionScenarios';
  gridSettings: GridSetting;
  selectedScenario: ScenarioResponseDto;
  instanceId: string;

  private _selectedItems: ScenarioResponseDto[];
  public get selectedItems(): ScenarioResponseDto[] {
    return this._selectedItems;
  }
  public set selectedItems(value: ScenarioResponseDto[]) {
    this._selectedItems = value;
  }
  private readonly _gridSettingsService = inject(GridSettingsService);
  private setLoading: (loading: boolean) => void;

  constructor(
    private readonly _spinnerService: SpinnerService,
    private readonly _modelVersionService: ModelVersionService,
    private readonly _modelVersionSharedService: ModelVersionSharedService,
    private readonly _dialogService: DialogService,
    private readonly _translate: TranslateService
  ) {
    this.setLoading = this._spinnerService.buildSetLoadingFn();
  }

  ngOnInit(): void {
    this._gridSettingsService
      .getGridSettingsByName(this.gridSettingKey)
      .subscribe((gridSettings) => (this.gridSettings = gridSettings));

    this._modelVersionSharedService.selectionInstance$.subscribe({
      next: (value: string) => {
        this.instanceId = value;
      },
    });

    this._modelVersionSharedService.scenariosRemoved$.subscribe({
      next: (value: boolean) => {
        if (value) {
          this.reloadScenarios();
        }
      },
    });
  }

  onSetScenario(scenario: ScenarioResponseDto): void {
    this.selectedScenario = scenario;
  }

  onDeleteScenarios(scenarios: ScenarioResponseDto[]): void {
    const modelKey = this.getModelKey(scenarios);
    const confirmMessageKey = this.getConfirmDeleteMessage(modelKey);

    this._dialogService
      .showTranslatedDialogMessage({
        translateKey: confirmMessageKey,
        icon: 'warning',
      })
      .pipe(
        switchMap((result) => {
          if (result.result) {
            this.setLoading(true);
            return this._modelVersionService.deleteScenarios(
              this.instanceId,
              scenarios[0].snapshotId,
              scenarios.map((s) => s.scenarioId)
            );
          } else {
            return of(null);
          }
        }),
        finalize(() => {
          this.setLoading(false);
        })
      )
      .subscribe({
        next: (result) => {
          if (result) {
            this.scenarios = this.scenarios.filter(
              (existingScenario) =>
                !scenarios.some(
                  (deletedScenario) => deletedScenario.scenarioId === existingScenario.scenarioId
                )
            );
            const scenarioIds = scenarios.map((scenario) => scenario.scenarioId);
            this._modelVersionSharedService.updateScenariosOfSnapshots(
              this.instanceId,
              scenarios[0].snapshotId,
              scenarioIds
            );
            this._dialogService.showEntityActionSnackBar('delete', modelKey);
          }
        },
        error: (error) => {
          this._dialogService.showErrorMessage(error);
        },
      });
  }

  reloadScenarios(): void {
    if (this.scenarios.length > 0) {
      this.scenarios = [];
    }
  }

  getConfirmDeleteMessage(entityName: string): string {
    const entityNameTranslated = this._translate.instant(`common.entities.${entityName}`);
    return this._translate.instant(this.getConfirmDeleteMessageKey(), {
      entityName: entityNameTranslated,
    });
  }

  private getConfirmDeleteMessageKey(): string {
    return 'core.messages.delete-confirm';
  }

  private getModelKey(scenarios: ScenarioResponseDto[]): 'scenario.singular' | 'scenario.plural' {
    return scenarios.length > 1 ? 'scenario.plural' : 'scenario.singular';
  }
}
